import React from 'react';
import { MonitorSettingsPage } from '../Edit/MonitorSettings';
import {
  AnyAssetSubType,
  defaultResponseHandler,
  DraftMonitorProfile,
  Flex,
  FormGroup,
  Grid,
  insertObjIf,
  Loading,
  MonitorProfile,
  openNotificationWithIcon,
  Page,
  REQUIRED_MSG,
  useAssetTranslation,
  useUserinfo,
} from '@mss/mss-component-library';
import { Form } from 'formik-antd/lib/form';
import ProfileForm from './Form';
import SubmitButton from '../../SubmitButton';
import { Formik } from 'formik';
import { object, string } from 'yup';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Fetch from 'jsx/common/util/_fetch';
import { Modal, notification } from 'antd';
import { initialValues } from './util';
import Button from '../../Button';
import Breadcrumbs from '../../Breadcrumbs';
import { Trash } from 'react-feather';

export default function Detail() {
  const { monitorProfileId, assetType = 'field' } = useParams<any>();
  const { t } = useAssetTranslation();
  const [hasPermission] = useUserinfo();
  const queryKey = ['monitor-profile', monitorProfileId];
  const history = useHistory();
  const { url } = useRouteMatch();
  const canEdit = hasPermission('change_automationmonitorprofile');
  const profileQuery = useQuery({
    queryKey,
    enabled: hasPermission('view_automationmonitorprofile'),
    queryFn: async function (): Promise<DraftMonitorProfile | null> {
      const resp = await defaultResponseHandler<MonitorProfile>({
        t,
        request: Fetch.get(
          `/api/automation/monitor-profile/${monitorProfileId}/`
        ),
      });
      const { data } = resp;
      return {
        name: data.name,
        asset_type: data.asset_type,
        assetSubType: (data.asset_type.split('.')[1] as AnyAssetSubType) ?? '-',
      };
    },
  });

  const queryClient = useQueryClient();
  const profileUpdateMutation = useMutation({
    mutationFn: async (data: DraftMonitorProfile) => {
      const resp = await Fetch.patch(
        `/api/automation/monitor-profile/${monitorProfileId}/`,
        {
          body: JSON.stringify(data),
        }
      );
      if (resp.ok) {
        openNotificationWithIcon(
          notification.success,
          t('Profile updated'),
          ''
        );
        await queryClient.invalidateQueries(queryKey);
      } else {
        openNotificationWithIcon(
          notification.error,
          t('Failed to update profile'),
          ''
        );
      }
    },
  });

  const profileDeleteMutation = useMutation({
    mutationFn: async () => {
      const resp = await Fetch.delete(
        `/api/automation/monitor-profile/${monitorProfileId}/`
      );
      if (resp.ok) {
        openNotificationWithIcon(
          notification.success,
          t('Profile deleted'),
          ''
        );
        queryClient.removeQueries(queryKey);
        await queryClient.invalidateQueries(['monitor-profiles']);
        history.push(url.replace(`/${monitorProfileId}`, ''));
      } else {
        const error = await resp.text();

        openNotificationWithIcon(
          notification.error,
          t('Failed to delete profile'),
          error?.includes('still are assets linked')
            ? t('This monitor profile is still linked to assets')
            : ''
        );
      }
    },
  });

  if (profileQuery.isLoading) {
    return <Loading />;
  }
  return (
    <Page
      breadCrumbs={<Breadcrumbs label={profileQuery.data?.name ?? ''} />}
      main={
        <Formik
          initialValues={{
            ...initialValues(assetType),
            ...profileQuery.data,
          }}
          onSubmit={profileUpdateMutation.mutateAsync}
          validationSchema={object({
            name: string()
              .required(REQUIRED_MSG)
              .max(200, 'Max 200 characters'),
            asset_type: string().required(REQUIRED_MSG),
          })}
        >
          {({ values, errors, dirty }) => {
            return (
              <Form className={'mb-4'}>
                <Grid.Row>
                  <ProfileForm />
                  <Grid.Col width={2}>
                    <Flex h="end" v="center">
                      <FormGroup
                        className="mr-2 flex-auto w-3/4"
                        label={<Spacer />}
                        input={
                          <SubmitButton
                            className="w-full"
                            disabled={
                              !canEdit ||
                              Object.keys(errors).length > 0 ||
                              !dirty
                            }
                          >
                            {t('Update details')}
                          </SubmitButton>
                        }
                      />
                      <FormGroup
                        className="ml-2 flex-auto w-1/4"
                        label={<Spacer />}
                        input={
                          <Button
                            className="w-full"
                            ghost
                            disabled={!canEdit}
                            colorOverrides={{
                              text: '#fa4654',
                              border: 'transparent',
                            }}
                            onClick={() =>
                              Modal.confirm({
                                title: t('Delete monitor profile'),
                                content: t(
                                  'Are you sure you want to delete this monitor profile?'
                                ),
                                onOk: profileDeleteMutation.mutateAsync,
                              })
                            }
                            data-ta="DeleteMonitorProfile"
                          >
                            <Flex h="center" v="center">
                              <Trash size={14} />
                            </Flex>
                          </Button>
                        }
                      />
                    </Flex>
                  </Grid.Col>
                </Grid.Row>
                <h5 className="mb-4">{t('Monitor settings for profile')}</h5>
                <MonitorSettingsPage
                  overrideAssetParams={{
                    assetType,
                    ...insertObjIf(values.assetSubType !== '-', {
                      assetSubType: values.assetSubType as AnyAssetSubType,
                    }),
                  }}
                />
              </Form>
            );
          }}
        </Formik>
      }
    />
  );
}

const Spacer = () => <div className={'h-[22px]'} />;
